import { Upload, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  REGISTRATION_DOCUMENT_MAX_LABEL,
  registrationFileSizeError,
} from "@/lib/registration/file-limits";

type RegistrationFileDropzoneProps = {
  id: string;
  label: string;
  fileName?: string;
  hasError?: boolean;
  disabled?: boolean;
  onSelect: (file: File) => void;
  onReject: (message: string) => void;
  onClear: () => void;
};

export function RegistrationFileDropzone({
  id,
  label,
  fileName,
  hasError = false,
  disabled = false,
  onSelect,
  onReject,
  onClear,
}: RegistrationFileDropzoneProps) {
  const border = hasError ? "border-red-400" : "border-slate-300";

  return (
    <div className="space-y-1.5">
      <p className="text-xs text-slate-500">
        Maximum size: {REGISTRATION_DOCUMENT_MAX_LABEL}. PDF, Word, or image.
      </p>
      {fileName ? (
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
          <span className="flex-1 truncate">{fileName}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={onClear}
            className="h-7 w-7 shrink-0 p-0"
            aria-label="Remove file"
          >
            <XCircle className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <label
          htmlFor={id}
          className={`flex h-24 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors duration-200 hover:bg-slate-50 ${border} ${disabled ? "pointer-events-none opacity-50" : ""}`}
        >
          <span className="flex flex-col items-center gap-1 text-slate-500">
            <Upload className="h-5 w-5" />
            <span className="text-xs">Click to upload</span>
          </span>
        </label>
      )}
      <Input
        id={id}
        type="file"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        disabled={disabled}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (!file) return;
          const sizeError = registrationFileSizeError(file, "document", label);
          if (sizeError) {
            onReject(sizeError);
            return;
          }
          onSelect(file);
        }}
      />
    </div>
  );
}
